import { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import CustomInput from "global/components/CustomInput/CustomInput";
import { getAvatarColors } from "utils/avatarColors";
import filtersStyles from "../SignRequestsFilters.styles";
import {
  getQuickRangeHint,
  getQuickRangeStart,
  getToday,
  SIGN_REQUESTS_QUICK_RANGES,
  SIGN_REQUESTS_STATUS_OPTIONS,
  SignRequestsFilterKey,
  SignRequestsFilterLookups,
  SignRequestsFilters,
  SignRequestsOption,
  SignRequestsQuickRange,
  toFilterDate,
  toggleValue,
} from "../SignRequestsFiltersHelper";
import { getSignerInitials, getUiStatusTone } from "../SignRequestsRowHelper";
import { CalendarGlyph, CaretDownGlyph } from "./SignRequestsIcons";

const styles = filtersStyles;

type DateChoice = "any" | SignRequestsQuickRange | "custom";

/**
 * An avatar colour per sender rather than per initial: the shared palette is
 * keyed by letter, so the email is hashed onto a letter first. Everyone called
 * "D…" otherwise gets the same green.
 */
const getSenderColor = (email: string) => {
  let hash = 0;
  for (let index = 0; index < email.length; index += 1) {
    hash = (hash * 31 + email.charCodeAt(index)) % 26;
  }
  return getAvatarColors(String.fromCharCode(65 + hash)).bg;
};

const DATE_CHOICES: { key: DateChoice; label: string }[] = [
  { key: "any", label: "Any time" },
  ...SIGN_REQUESTS_QUICK_RANGES,
  { key: "custom", label: "Custom range" },
];

interface SignRequestsFilterPanelProps {
  category: SignRequestsFilterKey;
  /** The filters being edited — not applied until the caller says so. */
  draft: SignRequestsFilters;
  onChange: (change: Partial<SignRequestsFilters>) => void;
  lookups: SignRequestsFilterLookups;
  /**
   * Let a long checklist run to its full length instead of scrolling inside a
   * capped box — for the drawer, whose pane scrolls and has the height to give.
   */
  fill?: boolean;
  isLoadingTags: boolean;
  /** Sender and template lists are still being read. */
  isLoadingOptions: boolean;
  onManageTags: () => void;
  onCreateFirstTag: () => void;
}

/**
 * The editor for one filter. The same block sits inside a pill's popover on
 * the filter bar and in the right-hand pane of the "All filters" drawer, so
 * the two can never drift apart.
 *
 * Mounted fresh each time its category is shown, which is what resets the
 * search box and the "Custom range" choice.
 */
const SignRequestsFilterPanel = ({
  category,
  draft,
  onChange,
  lookups,
  fill = false,
  isLoadingTags,
  isLoadingOptions,
  onManageTags,
  onCreateFirstTag,
}: SignRequestsFilterPanelProps) => {
  const [query, setQuery] = useState("");
  /*
    "Custom range" is a choice in its own right before any date is typed, so it
    cannot be read back off the draft alone.
  */
  const [isCustomDate, setIsCustomDate] = useState(
    !draft.quick && Boolean(draft.from || draft.to),
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  if (category === "status") {
    return (
      <Box sx={styles.panelList}>
        {SIGN_REQUESTS_STATUS_OPTIONS.map((option) => {
          const checked = draft.status.includes(option.value);
          const tone = getUiStatusTone(option.value);
          return (
            <FormControlLabel
              key={option.value}
              sx={styles.panelOption(checked)}
              control={
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() =>
                    onChange({
                      status: toggleValue(draft.status, option.value),
                    })
                  }
                />
              }
              label={
                <Box component="span" sx={styles.panelStatusChip(tone.bg)}>
                  <Box component="span" sx={styles.panelStatusDot(tone.dot)} />
                  <Box
                    component="span"
                    sx={styles.panelStatusLabel(tone.color)}
                  >
                    {option.label}
                  </Box>
                </Box>
              }
            />
          );
        })}
      </Box>
    );
  }

  if (category === "date") {
    const current: DateChoice = isCustomDate
      ? "custom"
      : (draft.quick ?? "any");

    const choose = (choice: DateChoice) => {
      setIsCustomDate(choice === "custom");
      setIsCalendarOpen(false);
      if (choice === "custom") onChange({ quick: null });
      else if (choice === "any") onChange({ quick: null, from: "", to: "" });
      else {
        onChange({
          quick: choice,
          from: getQuickRangeStart(choice),
          to: getToday(),
        });
      }
    };

    return (
      <Box role="radiogroup" aria-label="Date created" sx={styles.panelList}>
        {DATE_CHOICES.map((choice) => {
          const checked = current === choice.key;
          return (
            <FormControlLabel
              key={choice.key}
              sx={styles.panelOption(checked)}
              control={
                <Radio
                  size="small"
                  checked={checked}
                  onChange={() => choose(choice.key)}
                />
              }
              label={
                <>
                  {choice.label}
                  <Box component="span" sx={styles.panelOptionHint}>
                    {choice.key === "any"
                      ? ""
                      : choice.key === "custom"
                        ? "Pick dates"
                        : getQuickRangeHint(choice.key)}
                  </Box>
                </>
              }
            />
          );
        })}
        {isCustomDate && (
          <Box sx={styles.panelCustomDates}>
            {/*
              The calendar the date chip on this screen has always used. Its
              own shortcuts are hidden: the rows above are the shortcuts here.
              The picker's trigger is borderless by design, so the field box
              around it is drawn here.
            */}
            <Box sx={styles.panelDateField(isCalendarOpen)}>
              <CustomDateRangePicker
                ariaLabel="Custom date range"
                emptyLabel="Select dates"
                dateRange={{
                  fromDate: draft.from || null,
                  toDate: draft.to || null,
                }}
                setDateRange={(range) =>
                  onChange({
                    from: toFilterDate(range.fromDate),
                    to: toFilterDate(range.toDate),
                    quick: null,
                  })
                }
                openDateRangePicker={isCalendarOpen}
                setOpenDateRangePicker={setIsCalendarOpen}
                hideShortcuts
                isClearable={false}
                sx={styles.panelDateTrigger(Boolean(draft.from && draft.to))}
                startIcon={<CalendarGlyph sx={styles.panelDateGlyph} />}
                endIcon={<CaretDownGlyph sx={styles.panelDateCaret} />}
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (category === "signer") {
    return (
      <Box sx={styles.panelField}>
        <CustomInput
          autoFocus
          name="signer"
          placeHolder="Name or email"
          value={draft.signer}
          propsToInputElement={{ "aria-label": "Signer name or email" }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ signer: event.target.value })
          }
        />
        <Box component="span" sx={styles.panelNote}>
          Matches the name or email address of any signer on the request.
        </Box>
      </Box>
    );
  }

  /* Sender, Template and Tags share one searchable checklist. */
  const isTags = category === "tags";
  const options: SignRequestsOption[] = isTags
    ? lookups.tags.map((tag) => ({
        value: tag.id,
        label: tag.name,
        color: tag.color,
      }))
    : category === "senders"
      ? lookups.senders
      : lookups.templates;
  const selected = draft[category];
  const loading = isTags ? isLoadingTags : isLoadingOptions;
  const noun = isTags
    ? "tags"
    : category === "senders"
      ? "senders"
      : "templates";

  if (options.length === 0) {
    if (loading) {
      return (
        <Box sx={styles.panelLoading}>
          <CircularProgress
            size={22}
            role="status"
            aria-label={`Loading ${noun}`}
          />
        </Box>
      );
    }
    return (
      <Box sx={styles.panelField}>
        <Box sx={styles.panelEmpty}>
          {isTags ? (
            <>
              Tags help you group requests.{" "}
              <Button
                variant="text"
                disableRipple
                onClick={onCreateFirstTag}
                sx={styles.panelEmptyLink}
              >
                Create your first tag.
              </Button>
            </>
          ) : (
            `There are no ${noun} to filter by yet.`
          )}
        </Box>
      </Box>
    );
  }

  /** Two people with one name are told apart by the email under it. */
  const isSharedLabel = (label: string) =>
    options.filter((option) => option.label === label).length > 1;

  const needle = query.trim().toLowerCase();
  const matches = options.filter(
    (option) => !needle || option.label.toLowerCase().includes(needle),
  );

  return (
    <Box sx={styles.panelField}>
      <CustomInput
        name={`${category}Search`}
        placeHolder={`Search ${noun}`}
        value={query}
        propsToInputElement={{ "aria-label": `Search ${noun}` }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setQuery(event.target.value)
        }
      />
      <Box sx={styles.panelScrollList(fill)}>
        {matches.map((option) => {
          const checked = selected.includes(option.value);
          return (
            <FormControlLabel
              key={option.value}
              sx={styles.panelOption(checked)}
              control={
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() =>
                    onChange({
                      [category]: toggleValue(selected, option.value),
                    } as Partial<SignRequestsFilters>)
                  }
                />
              }
              label={
                option.color ? (
                  <Box component="span" sx={styles.panelTag(option.color)}>
                    {option.label}
                  </Box>
                ) : (
                  <>
                    {category === "senders" && (
                      <Avatar
                        sx={styles.panelAvatar(getSenderColor(option.value))}
                      >
                        {getSignerInitials(option.label)}
                      </Avatar>
                    )}
                    {category === "senders" && isSharedLabel(option.label) ? (
                      <Box component="span" sx={styles.panelOptionStack}>
                        <Box component="span" sx={styles.panelOptionText}>
                          {option.label}
                        </Box>
                        <Box component="span" sx={styles.panelOptionSub}>
                          {option.value}
                        </Box>
                      </Box>
                    ) : (
                      <Box component="span" sx={styles.panelOptionText}>
                        {option.label}
                      </Box>
                    )}
                  </>
                )
              }
            />
          );
        })}
        {matches.length === 0 && (
          <Box sx={styles.panelNoMatch}>No matches.</Box>
        )}
      </Box>
      {isTags && (
        <Box sx={styles.panelManageLink}>
          <Button
            variant="text"
            disableRipple
            onClick={onManageTags}
            sx={styles.manageLink}
          >
            Manage tags
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SignRequestsFilterPanel;
